import { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { captureException } from '../services/sentry';

/**
 * Route error element for handling navigation / lazy-chunk loading errors.
 *
 * Renders a friendly 404 for missing routes and a generic recovery screen for
 * everything else. Unexpected errors (i.e. anything that isn't a normal 404
 * navigation) are reported to Sentry so route-level failures — most importantly
 * lazy chunk-load failures after a deploy — are observable instead of silent.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (error && !is404) {
      captureException(error, {
        level: 'error',
        tags: { errorBoundary: 'route' },
      });
    }
  }, [error, is404]);

  if (is404) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-lg w-full p-8 text-center">
          <div className="text-8xl font-bold text-slate-200 dark:text-slate-700 select-none mb-8">
            404
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Page not found
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">
            The page you're looking for doesn't exist.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-6 rounded-lg transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-lg w-full p-8 text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Something went wrong
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-8">
          An unexpected error occurred. Please try again.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-6 rounded-lg transition"
          >
            Reload Page
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-white font-bold py-3 px-6 rounded-lg transition"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default RouteErrorBoundary;
